import { mutate, withBase } from './api'
import type { Customer } from './types'
import { undoPatch } from './undo'
import { el, shortDate, toast } from './ui'

// "What they said": the reason a customer gave the last time they were asked (Liam, 29 Sep 2026: "not yet,
// my neighbour is having work done", "not at the moment, I'm in hospital"), kept on the customer so it is in
// front of the owner the next time they message them, with no reading back through WhatsApp. Only the owner
// sees it: workers never read customers, and Today does not carry it (migration 0032).

type SaidCustomer = Pick<Customer, 'id' | 'name' | 'version' | 'said_note' | 'said_on'>

// "29 Sep: neighbour having work done", or '' when there is none.
export function saidLine(c: Pick<Customer, 'said_note' | 'said_on'>): string {
  const note = c.said_note?.trim()
  if (!note) return ''
  return c.said_on ? `${shortDate(c.said_on)}: ${note}` : note
}

// The box that asks for it, empty or holding what was said before.
export function saidInput(c: Pick<Customer, 'said_note'> | null, label = 'What did they say? (optional)'): HTMLInputElement {
  const input = el('input', { type: 'text', class: 'said-input', maxlength: '500', placeholder: 'e.g. Neighbour having work done', 'aria-label': label }) as HTMLInputElement
  input.value = c?.said_note ?? ''
  return input
}

// The note shown on a Messages card: what they said last time, with Change; or "Add what they said".
// Saving sends only the note (the server dates it) under the customer's version, with Undo.
export function saidBlock(c: SaidCustomer, after: () => void): HTMLElement {
  const box = el('div', { class: 'said-block' })
  const show = (): void => {
    box.replaceChildren()
    const line = saidLine(c)
    const edit = el('button', { type: 'button', class: 'link-button said-edit' }, line ? 'Change' : 'Add what they said')
    edit.addEventListener('click', open)
    if (line) box.append(el('p', { class: 'said-note' }, el('strong', {}, 'What they said '), line, ' ', edit))
    else box.append(edit)
  }
  const open = (): void => {
    box.replaceChildren()
    const input = saidInput(c)
    const save = el('button', { type: 'button', class: 'btn-primary' }, 'Save')
    const cancel = el('button', { type: 'button', class: 'btn-secondary' }, 'Cancel')
    cancel.addEventListener('click', show)
    save.addEventListener('click', () => {
      const text = input.value.trim()
      if (text === (c.said_note ?? '').trim()) return show()
      save.disabled = true
      const was = { version: c.version, said_note: c.said_note ?? null, said_on: c.said_on ?? null }
      void mutate({
        label: `What they said: ${c.name}`,
        method: 'PATCH',
        path: `/api/customers/${c.id}`,
        body: withBase(c, { version: c.version, said_note: text || null }),
        onConflict: after,
      }).then((ack) => {
        if (ack.ok) {
          // The card's other buttons save under the customer's new version, not the one before this save.
          const row = ack.row as Partial<Customer> | undefined
          if (row) Object.assign(c, { version: row.version ?? c.version, said_note: row.said_note ?? null, said_on: row.said_on ?? null })
          undoPatch({ text: `Saved what ${c.name} said`, label: `Undo what they said: ${c.name}`, path: `/api/customers/${c.id}`, ack, version: was.version,
            body: { said_note: was.said_note, said_on: was.said_on }, after })
          after()
          return
        }
        // No signal: it is kept on this phone and sends itself when the signal comes back.
        if (ack.status === 0) { toast('Saved on this phone. It sends when you have signal.'); show(); return }
        save.disabled = false
        if (ack.status >= 400 && ack.status < 500 && ack.status !== 409) toast(`Couldn't save that: ${ack.error ?? 'refused'}`)
      })
    })
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') save.click(); if (e.key === 'Escape') show() })
    box.append(el('label', { class: 'said-label' }, 'What did they say?', input), el('div', { class: 'said-btns' }, save, cancel))
    input.focus()
  }
  show()
  return box
}
